import { useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Alert,
} from "@mui/material";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { Add as AddIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useFetch, useApiCall, useApiErrorNotify } from "@/hooks";
import { membershipsApi } from "@/api";
import { useNotification } from "@/context/NotificationContext";
import ConfirmDialog from "@/components/ConfirmDialog";
import PageLoadingState from "@/components/PageLoadingState";
import { DataGridActionsCell } from "@/components/DataGridActionsCell";
import {
  DATA_GRID_INITIAL_PAGE_SIZE,
  DATA_GRID_PAGE_SIZE_OPTIONS,
} from "@/constants";
import { EMPTY_CELL_PLACEHOLDER } from "@/constants/display";
import type { Membership } from "@/types";

const EMPTY: Partial<Membership> = { name: "", type: "", cost: undefined };

export default function Memberships() {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const onApiError = useApiErrorNotify();
  const {
    data: memberships,
    loading,
    error,
    refresh,
  } = useFetch<Membership[]>(membershipsApi.getMemberships);

  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<Membership>>(EMPTY);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { execute: doCreate, loading: creating } = useApiCall(
    membershipsApi.createMembership,
    { onError: onApiError },
  );
  const { execute: doUpdate, loading: updating } = useApiCall(
    membershipsApi.updateMembership,
    { onError: onApiError },
  );
  const { execute: doDelete, loading: deleting } = useApiCall(
    membershipsApi.deleteMembership,
    { onError: onApiError },
  );

  const openCreate = () => {
    setEditId(null);
    setForm(EMPTY);
    setFormOpen(true);
  };
  const openEdit = (m: Membership) => {
    setEditId(m.id);
    setForm({ name: m.name, type: m.type, cost: m.cost ?? undefined });
    setFormOpen(true);
  };

  const handleSave = async () => {
    const payload = {
      ...form,
      cost: form.cost ? Number(form.cost) : undefined,
    };
    const ok = editId
      ? await doUpdate(editId, payload)
      : await doCreate(payload);
    if (ok !== undefined) {
      notify(t("common.success"), "success");
      setFormOpen(false);
      refresh();
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const ok = await doDelete(deleteId);
    if (ok !== undefined) {
      notify(t("common.success"), "success");
      setDeleteId(null);
      refresh();
    }
  };

  const setField = useCallback((field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const columns = useMemo<GridColDef[]>(
    () => [
      { field: "name", headerName: t("common.name"), flex: 1, minWidth: 150 },
      { field: "type", headerName: t("common.type"), flex: 1, minWidth: 120 },
      {
        field: "cost",
        headerName: t("common.cost"),
        width: 120,
        valueFormatter: (v: number | null) =>
          v != null ? `€${v}` : EMPTY_CELL_PLACEHOLDER,
      },
      {
        field: "actions",
        headerName: t("common.actions"),
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <DataGridActionsCell
            onEdit={() => openEdit(params.row)}
            onDelete={() => setDeleteId(params.row.id)}
          />
        ),
      },
    ],
    [t],
  );

  if (loading) return <PageLoadingState />;

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
          }}
        >
          {t("nav.memberships")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
        >
          {t("common.create")}
        </Button>
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <DataGrid
        rows={Array.isArray(memberships) ? memberships : []}
        columns={columns}
        pageSizeOptions={[...DATA_GRID_PAGE_SIZE_OPTIONS]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: DATA_GRID_INITIAL_PAGE_SIZE },
          },
        }}
        disableRowSelectionOnClick
        disableColumnResize
      />
      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editId ? t("common.edit") : t("common.create")}
        </DialogTitle>
        <DialogContent>
          <Stack
            spacing={2}
            sx={{
              mt: 1,
            }}
          >
            <TextField
              label={t("common.name")}
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              fullWidth
              required
              size="small"
            />
            <TextField
              label={t("common.type")}
              value={form.type}
              onChange={(e) => setField("type", e.target.value)}
              fullWidth
              required
              size="small"
            />
            <TextField
              label={t("common.cost")}
              value={form.cost ?? ""}
              onChange={(e) => setField("cost", e.target.value)}
              fullWidth
              size="small"
              type="number"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={creating || updating}
          >
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={!!deleteId}
        title={t("common.delete")}
        message={t("dialogs.deleteConfirm")}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
        loading={deleting}
      />
    </Box>
  );
}
